import React, { useCallback, useEffect, useRef, useState } from "react";
import "./invoice.css";
import {
  downloadPdfById,
  handleGetApiGF,
  rupeesFn,
  Toast,
} from "../Utils/general-function";
import Loader from "../Utils/Loader";

const Invoice = ({ setIsTrue, isTrue, transactionId }) => {
  const [loader, setLoader] = useState(false);
  const [invoice, setInvoice] = useState({});

  // ✅ Fetch API (stable)
  const fetchInvoice = useCallback(async () => {
    if (!transactionId) {
      Toast(false, "", "Transaction ID is missing");
      return null;
    }

    const res = await handleGetApiGF({
      url: `/api/WalletTransaction/AllWalletInvoice?walletTransactionid=${transactionId}`,
    });

    if (!res?.succ) {
      Toast(false, "", res?.msg || "Failed to fetch invoice data");
      return null;
    }

    return res?.data?.Data?.[0] || null;
  }, [transactionId]);

  // Main flow
  const handleProcess = useCallback(async () => {
    setLoader(true);

    try {
      const data = await fetchInvoice();
      if (!data) return;

      setInvoice(data);

      // ✅ Wait for DOM paint properly (better than RAF hack)
      await new Promise(requestAnimationFrame);
      await new Promise(requestAnimationFrame);
      // await new Promise((resolve) => setTimeout(resolve, 0));

      await downloadPdfById({ id: "invoicepdf", filename: data?.id });
    } catch (err) {
      console.error(err);
      Toast(false, "", "Something went wrong");
    } finally {
      setLoader(false);
      setIsTrue(false);
    }
  }, [fetchInvoice, setIsTrue]);

  // ✅ Trigger only when needed
  useEffect(() => {
    if (isTrue && transactionId) {
      handleProcess();
    }
  }, [isTrue, transactionId, handleProcess]);

  if (!isTrue) return null;

  const {
    id,
    comp_name,
    comp_ownername,
    comp_address,
    comp_city,
    comp_state,
    comp_pincode,
    comp_country,
    comp_email,
    comp_mobileno,
    comp_gstno,
    comp_gstpercentage,
    dealer_id,
    dealer_comp_name,
    dealer_address,
    dealer_city,
    dealer_pincode,
    dealer_state,
    dealer_country,
    dealer_gstno,
    walletTransactionid,
    nStatus,
    nRand,
    dCreatedate,
  } = invoice || {};

  return (
    <>
      {loader && <Loader />}
      <div className="invoice-wrapper">
        {/* Invoice Page */}
        <div id="invoicepdf" className="invoice-page">
          <div className="invoice-page-inner">
            <div className="invoice-table">
              <div className="invoice-row">
                <div className="invoice-cell">
                  <div className="header-block">
                    <div className="invoice-row">
                      <div className="header-logo-cell">
                        {/* Logo placeholder */}
                        <img
                          src="/assets/images/logo.png"
                          alt="logo"
                          className="src"
                          width={150}
                          crossOrigin="anonymous"
                        />
                      </div>
                      <button
                        style={{
                          backgroundColor: "#30a400",
                          color: "white",
                          border: "none",
                          padding: "5px 8px",
                          borderRadius: "6px",
                        }}
                      >
                        Paid receipt
                      </button>
                    </div>
                  </div>

                  {/* Seller & Buyer Info */}
                  <div className="parties-block">
                    <div className="invoice-row">
                      {/* Seller */}
                      <div className="seller-cell">
                        <strong>{comp_name}</strong>
                        <br />
                        {comp_ownername}
                        <br />
                        GSTIN No : {comp_gstno}
                        <br />
                        {comp_address}
                        <br />
                        {comp_country}
                        <br />
                        {comp_state}
                        <br />
                        {comp_city}
                        <br />
                        {comp_pincode}
                      </div>

                      {/* Buyer */}
                      <div className="buyer-cell">
                        <table className="info-table">
                          <tbody>
                            <tr>
                              <td>Buyer Name</td>
                              <td className="colon-cell">:</td>
                              <td>{dealer_comp_name}</td>
                            </tr>

                            <tr>
                              <td>GST No.</td>
                              <td className="colon-cell">:</td>
                              <td>{dealer_gstno || "Not applicable"}</td>
                            </tr>

                            <tr>
                              <td>Address</td>
                              <td className="colon-cell">:</td>
                              <td>
                                {dealer_address}
                                <br />
                                {dealer_country}
                                <br />
                                {dealer_state}
                                <br />
                                {dealer_city}
                                <br />
                                {dealer_pincode}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* GSTIN / Invoice Meta */}
                  <div className="meta-block">
                    <div className="invoice-row">
                      {/* Left: Company contact */}
                      {/* <div className="meta-left-cell">
                        <table className="info-table">
                          <tbody>
                            <tr>
                              <td>GSTIN No</td>
                              <td className="colon-cell">:</td>
                              <td>{comp_gstno}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div> */}

                      {/* Right: Invoice details */}
                      <div className="meta-right-cell">
                        <table className="info-table w-100">
                          <tbody className="d-flex justify-content-between">
                            <tr>
                              <td>Invoice No</td>
                              <td className="colon-cell">:</td>
                              <td>{id}</td>
                            </tr>
                            <tr>
                              <td>Date</td>
                              <td className="colon-cell">:</td>
                              <td>
                                {new Date(dCreatedate).toLocaleString("en-IN")}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Line Items */}
                  <div className="items-block">
                    <div className="invoice-row">
                      <div className="items-cell">
                        <table className="bd-table">
                          <thead>
                            <tr>
                              <th className="desc-col">Description</th>
                              <th className="amount-col">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Service Charge</td>
                              <td className="amount-right pe-1">
                                {rupeesFn(
                                  invoice?.transactiondetailslist?.[0]
                                    ?.dcAmount_withoutgst || 0,
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="totals-block">
                    <div className="invoice-row">
                      <div className="totals-spacer-cell" />
                      <div className="totals-values-cell">
                        <table className="totals-inner-table">
                          <tbody>
                            <tr>
                              <td className="stotal">Sub Total</td>
                              <td className="samount">
                                {" "}
                                {rupeesFn(
                                  invoice?.transactiondetailslist?.[0]
                                    ?.dcAmount_withoutgst || 0,
                                )}
                              </td>
                            </tr>
                            {dealer_state === "Gujarat" ? (
                              <>
                                <tr>
                                  <td className="stotal">
                                    SGST Rate - (
                                    {(invoice?.transactiondetailslist?.[0]
                                      ?.dcGstPercentage || 0) / 2}
                                    %)
                                  </td>
                                  <td className="samount">
                                    {" "}
                                    {rupeesFn(
                                      (invoice?.transactiondetailslist?.[0]
                                        ?.dcTaxAmount || 0) / 2,
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="stotal">
                                    CGST Rate - (
                                    {(invoice?.transactiondetailslist?.[0]
                                      ?.dcGstPercentage || 0) / 2}
                                    %)
                                  </td>
                                  <td className="samount">
                                    {" "}
                                    {rupeesFn(
                                      (invoice?.transactiondetailslist?.[0]
                                        ?.dcTaxAmount || 0) / 2,
                                    )}
                                  </td>
                                </tr>
                              </>
                            ) : (
                              <tr>
                                <td className="stotal">
                                  GST Rate - (
                                  {invoice?.transactiondetailslist?.[0]
                                    ?.dcGstPercentage || 0}
                                  %)
                                </td>
                                <td className="samount">
                                  {" "}
                                  {rupeesFn(
                                    invoice?.transactiondetailslist?.[0]
                                      ?.dcTaxAmount || 0,
                                  )}
                                </td>
                              </tr>
                            )}
                            <tr className="total-row">
                              <td className="stotal">Total</td>
                              <td className="samount">
                                {rupeesFn(
                                  invoice?.transactiondetailslist?.[0]
                                    ?.dcFinalAmount || 0,
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div className="terms-block">
                    <div className="terms-heading-row">
                      <div className="terms-heading-cell">
                        Terms & Conditions
                      </div>
                    </div>
                    <div className="invoice-row">
                      <div className="terms-body-cell">
                        <ol className="terms-list">
                          <li>Amount is non refundable.</li>
                          <li>Subject to Ahmedabad Jurisdiction.</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoice;

// old with mine
// import React, { useEffect, useRef, useState } from "react";
// import "./invoice.css";
// import {
//   downloadPdfById,
//   handleGetApiGF,
//   rupeesFn,
//   Toast,
//   urltobase64,
// } from "../Utils/general-function";
// import { Modal } from "react-bootstrap";
// import Loader from "../Utils/Loader";

// const Invoice = ({ setIsTrue, isTrue, transactionId }) => {
//   const [loader, setLoader] = useState(false);
//   const [invoice, setInvoice] = useState({});
//   console.log(isTrue, transactionId);

//   const fetchInvoice = async () => {
//     if (!transactionId) {
//       Toast(false, "", "Transaction ID is missing");
//       return;
//     }
//     const res = await handleGetApiGF({
//       url:
//         "/api/WalletTransaction/AllWalletInvoice?walletTransactionid=" +
//         transactionId,
//     });

//     if (!res?.succ) {
//       Toast(false, "", res?.msg || "Failed to fetch invoice data");
//       setLoader(false);
//       setIsTrue(false);
//       return;
//     }
//     return res?.data?.Data?.[0] || {};
//   };
//   const handleDownload = async () => {
//     setLoader(true);
//     const invoiceData = await fetchInvoice();
//     if (!invoiceData) return;
//     setInvoice(invoiceData);
//     await new Promise(requestAnimationFrame);
//     await new Promise(requestAnimationFrame);
//     await downloadPdfById("invoicepdf");
//     setLoader(false);
//     setIsTrue(false);
//   };
//   console.log("invoice", invoice);

//   useEffect(() => {
//     if (isTrue && transactionId) {
//       handleDownload();
//     }
//   }, [isTrue, transactionId]);
//   const {
//     id,
//     comp_name,
//     comp_ownername,
//     comp_address,
//     comp_city,
//     comp_state,
//     comp_pincode,
//     comp_country,
//     comp_email,
//     comp_mobileno,
//     comp_gstno,
//     comp_gstpercentage,
//     dealer_id,
//     dealer_comp_name,
//     dealer_address,
//     dealer_city,
//     dealer_pincode,
//     dealer_state,
//     dealer_country,
//     dealer_gstno,
//     walletTransactionid,
//     nStatus,
//     nRand,
//     dCreatedate,
//   } = invoice || {};
//   return (
//     <>
//       {loader && <Loader />}
//       {isTrue && (
//         <div className="invoice-wrapper">
//           {/* Action Buttons */}
//           <div className="invoice-btn-group">
//             <div className="invoice-btn-inner"></div>
//           </div>

//           {/* Invoice Page */}
//           <div id="invoicepdf" className="invoice-page">
//             <div className="invoice-page-inner">
//               <div className="invoice-table">
//                 <div className="invoice-row">
//                   <div className="invoice-cell">
//                     {/* Header: Logo + Paid Receipt Badge */}
//                     <div className="header-block">
//                       <div className="invoice-row">
//                         <div className="header-logo-cell">
//                           {/* Logo placeholder */}
//                           <img
//                             src="/assets/images/logo.png"
//                             alt="logo"
//                             className="src"
//                             width={100}
//                             crossOrigin="anonymous"
//                           />
//                         </div>
//                         {/* <div className="header-badge-cell">
//                             <div className="paid-receipt-badge">
//                               PAID RECEIPT
//                             </div>
//                           </div> */}
//                       </div>
//                     </div>

//                     {/* Seller & Buyer Info */}
//                     <div className="parties-block">
//                       <div className="invoice-row">
//                         {/* Seller */}
//                         <div className="seller-cell">
//                           <strong>{comp_name}</strong>
//                           <br />
//                           {comp_ownername}
//                           <br />
//                           {comp_address}
//                           <br />
//                           {comp_country}
//                           <br />
//                           {comp_state}
//                           <br />
//                           {comp_city}
//                           <br />
//                           {comp_pincode}
//                         </div>

//                         {/* Buyer */}
//                         <div className="buyer-cell">
//                           <table className="info-table">
//                             <tbody>
//                               <tr>
//                                 <td>Buyer Name</td>
//                                 <td className="colon-cell">:</td>
//                                 <td>{dealer_comp_name}</td>
//                               </tr>
//                               {/* <tr>
//                                 <td>GST No.</td>
//                                 <td className="colon-cell">:</td>
//                                 <td>{dealer_gstno}</td>
//                               </tr> */}
//                               <tr>
//                                 <td>Address</td>
//                                 <td className="colon-cell">:</td>
//                                 <td>
//                                   {dealer_address}
//                                   <br />
//                                   {dealer_country}
//                                   <br />
//                                   {dealer_state}
//                                   <br />
//                                   {dealer_city}
//                                   <br />
//                                   {dealer_pincode}
//                                 </td>
//                               </tr>
//                             </tbody>
//                           </table>
//                         </div>
//                       </div>
//                     </div>

//                     {/* GSTIN / Invoice Meta */}
//                     <div className="meta-block">
//                       <div className="invoice-row">
//                         {/* Left: Company contact */}
//                         <div className="meta-left-cell">
//                           <table className="info-table">
//                             <tbody>
//                               <tr>
//                                 <td>GSTIN No</td>
//                                 <td className="colon-cell">:</td>
//                                 <td>{comp_gstno}</td>
//                               </tr>
//                             </tbody>
//                           </table>
//                         </div>

//                         {/* Right: Invoice details */}
//                         <div className="meta-right-cell">
//                           <table className="info-table">
//                             <tbody>
//                               <tr>
//                                 <td>Invoice No</td>
//                                 <td className="colon-cell">:</td>
//                                 <td>{id}</td>
//                               </tr>
//                               <tr>
//                                 <td>Date</td>
//                                 <td className="colon-cell">:</td>
//                                 <td>
//                                   {new Date(dCreatedate).toLocaleString(
//                                     "en-IN",
//                                   )}
//                                 </td>
//                               </tr>
//                             </tbody>
//                           </table>
//                         </div>
//                       </div>
//                     </div>

//                     {/* Line Items */}
//                     <div className="items-block">
//                       <div className="invoice-row">
//                         <div className="items-cell">
//                           <table className="bd-table">
//                             <thead>
//                               <tr>
//                                 <th className="desc-col">Description</th>
//                                 <th className="amount-col">Amount</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               <tr>
//                                 <td>Service Charge</td>
//                                 <td className="amount-right">
//                                   {rupeesFn(
//                                     invoice?.transactiondetailslist?.[0]
//                                       ?.dcAmount_withoutgst || 0,
//                                   )}
//                                 </td>
//                               </tr>
//                             </tbody>
//                           </table>
//                         </div>
//                       </div>
//                     </div>

//                     {/* Totals */}
//                     <div className="totals-block">
//                       <div className="invoice-row">
//                         <div className="totals-spacer-cell" />
//                         <div className="totals-values-cell">
//                           <table className="totals-inner-table">
//                             <tbody>
//                               <tr>
//                                 <td className="stotal">Sub Total</td>
//                                 <td className="samount">
//                                   {" "}
//                                   {rupeesFn(
//                                     invoice?.transactiondetailslist?.[0]
//                                       ?.dcAmount_withoutgst || 0,
//                                   )}
//                                 </td>
//                               </tr>
//                               <tr>
//                                 <td className="stotal">
//                                   GST Rate - (
//                                   {invoice?.transactiondetailslist?.[0]
//                                     ?.dcGstPercentage || 0}
//                                   %)
//                                 </td>
//                                 <td className="samount">
//                                   {" "}
//                                   {rupeesFn(
//                                     invoice?.transactiondetailslist?.[0]
//                                       ?.dcGstPercentage || 0,
//                                   )}
//                                 </td>
//                               </tr>
//                               <tr className="total-row">
//                                 <td className="stotal">Total</td>
//                                 <td className="samount">
//                                   {rupeesFn(
//                                     invoice?.transactiondetailslist?.[0]
//                                       ?.dcFinalAmount || 0,
//                                   )}
//                                 </td>
//                               </tr>
//                             </tbody>
//                           </table>
//                         </div>
//                       </div>
//                     </div>

//                     {/* Terms & Conditions */}
//                     <div className="terms-block">
//                       <div className="terms-heading-row">
//                         <div className="terms-heading-cell">
//                           Terms & Conditions
//                         </div>
//                       </div>
//                       <div className="invoice-row">
//                         <div className="terms-body-cell">
//                           <ol className="terms-list">
//                             <li>Amount is non refundable.</li>
//                             <li>Subject to Palanpur Jurisdiction.</li>
//                           </ol>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//       {/* <Modal
//         show={isTrue}
//         onHide={() => {
//           setIsTrue(false);
//         }}
//       >
//         <Modal.Header closeButton>
//           <h3 className="fs-3 me-3 mb-0">Invoice</h3>
//           <button
//             className="submitbtn py-1 px-4"
//             onClick={() => handleDownload()}
//           >
//             Download
//           </button>
//         </Modal.Header>
//         <Modal.Body> </Modal.Body>
//       </Modal> */}
//     </>
//   );
// };

// export default Invoice;
